import React from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Mail, Target } from 'lucide-react';
import { getLocalizedBlogPosts, useLanguage } from '../i18n';
import {
    FaCalendarAlt,
    FaClock,
    FaUser,
    FaArrowRight,
    FaTag,
    FaBookOpen
} from 'react-icons/fa';
import { handleImageError } from '../utils/imageFallback';

const Blog = () => {
    const { language, t } = useLanguage();
    const blogPosts = getLocalizedBlogPosts(language);
    const scrollToContact = () => {
        const element = document.getElementById('contact');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(language === 'vi' ? 'vi-VN' : 'en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getCategoryColor = (category) => {
        const colors = {
            'SEO': 'bg-green-100 text-green-700',
            'Content Marketing': 'bg-blue-100 text-blue-700',
            'Security': 'bg-red-100 text-red-700',
            'E-commerce': 'bg-purple-100 text-purple-700',
            'Design': 'bg-pink-100 text-pink-700'
        };
        return colors[category] || 'bg-gray-100 text-gray-700';
    };

    return (
        <section id="blog" className="py-20 bg-white">
            <div className="container mx-auto px-4">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-16"
                >
                    <div className="flex items-center justify-center mb-6">
                        <FaBookOpen className="text-4xl text-primary-600 mr-4" />
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
                            {t('blogTitle')}<span className="text-primary-600">{t('blogTitleAccent')}</span>
                        </h2>
                    </div>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                        {t('blogDescription')}
                    </p>

                    {/* Blog Categories Filter */}
                    <div className="flex flex-wrap justify-center gap-3">
                        {[t('all'), 'SEO', language === 'vi' ? 'Content Marketing' : 'Content marketing', language === 'vi' ? 'Bảo mật' : 'Security', 'E-commerce', language === 'vi' ? 'Thiết kế' : 'Design'].map((category, index) => (
                            <motion.button
                                key={category}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1, duration: 0.5 }}
                                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${index === 0
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-primary-100 hover:text-primary-600'
                                    }`}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                {category}
                            </motion.button>
                        ))}
                    </div>
                </motion.div>

                {/* Featured Post */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-3xl overflow-hidden mb-16 text-white"
                >
                    <div className="grid lg:grid-cols-2">
                        <div className="p-8 lg:p-12">
                            <div className="flex items-center mb-4">
                                <span className="bg-white bg-opacity-20 text-white px-3 py-1 rounded-full text-sm font-medium">
                                    {t('featuredPost')}
                                </span>
                            </div>

                            <h3 className="text-3xl lg:text-4xl font-bold mb-4">
                                {blogPosts[0].title}
                            </h3>

                            <p className="text-primary-100 text-lg mb-6 leading-relaxed">
                                {blogPosts[0].excerpt}
                            </p>

                            <div className="flex items-center space-x-6 mb-8 text-primary-100">
                                <span className="flex items-center">
                                    <FaUser className="mr-2" />
                                    {blogPosts[0].author}
                                </span>
                                <span className="flex items-center">
                                    <FaCalendarAlt className="mr-2" />
                                    {formatDate(blogPosts[0].date)}
                                </span>
                                <span className="flex items-center">
                                    <FaClock className="mr-2" />
                                    {blogPosts[0].readTime}
                                </span>
                            </div>

                            <motion.button
                                className="bg-white text-primary-600 hover:bg-primary-50 px-6 py-3 rounded-lg font-semibold transition-colors inline-flex items-center group"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                {t('readArticle')}
                                <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                            </motion.button>
                        </div>

                        <div className="relative h-64 lg:h-auto">
                            <img
                                src={blogPosts[0].image}
                                alt={blogPosts[0].title}
                                className="w-full h-full object-cover"
                                onError={handleImageError}
                            />
                        </div>
                    </div>
                </motion.div>

                {/* Blog Posts Grid */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
                >
                    {blogPosts.slice(1).map((post, index) => (
                        <motion.article
                            key={post.id}
                            variants={itemVariants}
                            className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer"
                        >
                            {/* Post Image */}
                            <div className="relative overflow-hidden">
                                <img
                                    src={post.image}
                                    alt={post.title}
                                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                                    onError={handleImageError}
                                />
                                <div className="absolute top-4 left-4">
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(post.category)}`}>
                                        {post.category}
                                    </span>
                                </div>
                            </div>

                            {/* Post Content */}
                            <div className="p-6">
                                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                                    {post.title}
                                </h3>

                                <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">
                                    {post.excerpt}
                                </p>

                                {/* Post Meta */}
                                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                                    <span className="flex items-center">
                                        <FaUser className="mr-1" />
                                        {post.author}
                                    </span>
                                    <span className="flex items-center">
                                        <FaClock className="mr-1" />
                                        {post.readTime}
                                    </span>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-500 flex items-center">
                                        <FaCalendarAlt className="mr-1" />
                                        {formatDate(post.date)}
                                    </span>

                                    <motion.button
                                        className="text-primary-600 hover:text-primary-700 font-medium text-sm inline-flex items-center group"
                                        whileHover={{ x: 5 }}
                                    >
                                        {t('readMore')}
                                        <FaArrowRight className="ml-1 text-xs group-hover:translate-x-1 transition-transform" />
                                    </motion.button>
                                </div>

                                {/* Tags */}
                                <div className="mt-4 pt-4 border-t border-gray-100">
                                    <div className="flex items-center space-x-2">
                                        <FaTag className="text-gray-400 text-xs" />
                                        <div className="flex flex-wrap gap-1">
                                            {post.tags.slice(0, 2).map((tag, tagIndex) => (
                                                <span
                                                    key={tagIndex}
                                                    className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded"
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                            {post.tags.length > 2 && (
                                                <span className="text-xs text-gray-400">
                                                    +{post.tags.length - 2} {t('more')}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.article>
                    ))}
                </motion.div>

                {/* Newsletter Subscription */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-3xl p-12 text-center"
                >
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">
                        {language === 'vi' ? 'Cập nhật với Mẹo WordPress Mới Nhất' : 'Get the latest WordPress tips'}
                    </h3>
                    <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
                        {language === 'vi' ? 'Đăng ký nhận bản tin của chúng tôi và nhận những thông tin mới nhất về WordPress, hướng dẫn và tin tức ngành được gửi đến hộp thư của bạn.' : 'Subscribe for the latest WordPress insights, guides, and industry news delivered to your inbox.'}
                    </p>

                    <div className="max-w-md mx-auto mb-6">
                        <div className="flex gap-3">
                            <input
                                type="email"
                                placeholder={language === 'vi' ? 'Nhập email của bạn' : 'Enter your email address'}
                                className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-colors"
                            />
                            <motion.button
                                className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                {t('subscribe')}
                            </motion.button>
                        </div>
                        <p className="text-sm text-gray-500 mt-2">
                            {language === 'vi' ? 'Không bao giờ spam bạn. Hủy đăng ký bất cứ lúc nào.' : 'No spam. Unsubscribe anytime.'}
                        </p>
                    </div>

                    <div className="flex items-center justify-center space-x-8 text-sm text-gray-600">
                        <span className="flex items-center gap-2"><Mail size={16} aria-hidden="true" /> {language === 'vi' ? 'Bản tin hàng tuần' : 'Weekly newsletters'}</span>
                        <span className="flex items-center gap-2"><Target size={16} aria-hidden="true" /> {language === 'vi' ? 'Mẹo độc quyền' : 'Exclusive tips'}</span>
                        <span className="flex items-center gap-2"><BookOpen size={16} aria-hidden="true" /> {language === 'vi' ? 'Tài nguyên miễn phí' : 'Free resources'}</span>
                    </div>
                </motion.div>

                {/* Blog CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="text-center mt-16"
                >
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        {language === 'vi' ? 'Cần xây dựng website WordPress?' : 'Need a WordPress website?'}
                    </h3>
                    <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                        {language === 'vi' ? 'Đội ngũ chuyên gia WordPress của chúng tôi sẵn sàng giúp bạn tạo ra một trang web tuyệt vời mang lại kết quả cho doanh nghiệp của bạn.' : 'Our WordPress experts are ready to build a great website that delivers results for your business.'}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <motion.button
                            onClick={scrollToContact}
                            className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center group"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            {t('startProject')}
                            <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                        </motion.button>

                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Blog;
